import React from 'react'
import { Popup } from 'semantic-ui-react';

interface TooltipInfo {
  target: string
  benchmarks: { label: string; color: string }[]
}

interface MetricCardProps {
  label: string
  value: number
  unit?: string
  showDecimal?: boolean
  colorThreshold?: { green: number; yellow: number }
  inverse?: boolean // For metrics where lower is better (e.g., duplicates)
  tooltipInfo?: TooltipInfo // Optional tooltip information
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  unit = '',
  showDecimal = true,
  colorThreshold = { green: 95, yellow: 90 },
  inverse = false,

}) => {
  const getColorClass = (val: number): string => {
    if (inverse) {
      // For duplicates: lower is better (< 1% = green, 2-4% = yellow, >= 4% = red)
      if (val < 1) return 'green'
      if (val >= 2 && val < 4) return 'yellow'
      if (val >= 4) return 'red'
      // For values between 1% and 2% (1.x%), treat as green
      return 'green'
    } else {
      // For normal metrics: higher is better (> 95% = green, >= 90% = yellow, < 90% = red)
      if (val > colorThreshold.green) return 'green'
      if (val >= colorThreshold.yellow) return 'yellow'
      return 'red'
    }
  }

  const formattedValue = showDecimal ? value.toFixed(1) : value.toFixed(0)

  return (
    <div className="metric-card-item">
      <div className="metric-card-label">{label}</div>
      <div className="metric-card-value-container">
        <div className={`metric-card-value ${getColorClass(value)}`}>
          {formattedValue}{unit}
        </div>
        {label === "Duplicate (%)" && (
          <Popup
            trigger={
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="duplicate-info-icon"
              >
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
            }
            position="right center"
            on="hover"
            className="duplicate-popup"
          >
            <div className="duplicate-popup-content">
              <div className="duplicate-popup-section">
                <div className="duplicate-popup-title">Duplicate</div>
                <div className={`duplicate-popup-metric-value ${getColorClass(value)}`}>
                  {formattedValue}%
                </div>
              </div>
              <div className="duplicate-popup-section">
                <div className="duplicate-popup-title">Benchmarks</div>
                <div className="duplicate-benchmarks-list">
                  <div className="duplicate-benchmark-item">
                    <span className="duplicate-benchmark-dot green"></span>
                    <span className="duplicate-benchmark-label">&lt; 1%</span>
                  </div>
                  <div className="duplicate-benchmark-item">
                    <span className="duplicate-benchmark-dot yellow"></span>
                    <span className="duplicate-benchmark-label">2% to 4%</span>
                  </div>
                  <div className="duplicate-benchmark-item">
                    <span className="duplicate-benchmark-dot red"></span>
                    <span className="duplicate-benchmark-label">&gt;= 4%</span>
                  </div>
                </div>
              </div>
            </div>
          </Popup>
        )}
              
      </div>
    </div>
  )
}

export default MetricCard
